/**
 * WHERE THE RECOVERY LINK LANDS.
 *
 * This screen did not exist. resetPasswordForEmail was already sending people
 * to `${origin}/reset`, and the router's catch-all bounced /reset straight to
 * the portal — so anybody who did receive a link arrived holding a live
 * recovery session and was never asked to set a password. Half of "the link
 * doesn't work" was the send failing silently; this was the other half.
 *
 * HOW A RECOVERY LINK ACTUALLY WORKS, WHICH IS WHY THIS SCREEN IS SO PLAIN
 *
 * Supabase exchanges the token in the URL for a real session before this
 * component ever renders, and fires a PASSWORD_RECOVERY event. By the time
 * somebody is looking at this form they are, technically, signed in. There is
 * nothing to verify here and nothing to pass along: the only job is to call
 * updateUser({ password }) and get them out of the recovery state.
 *
 * That is also why this screen must not be reachable without one. A person who
 * navigates to /reset by hand while signed in normally would otherwise get a
 * password-change form with no current-password check — which is a session
 * hijack primitive, not a convenience. So it insists on a session and says so
 * when there is not one.
 *
 * The password rules are the founder's, unchanged and enforced in one place:
 * "minimum 8 alphanumeric with special character and Caps".
 */

import { useEffect, useState } from 'react';
import { supabase } from './lib/supabaseClient';
import { passwordProblems } from './lib/staffCredentials';
import { readableEmailError } from './lib/email';
import {
  O, SANS, DISPLAY, Card, PrimaryCTA, Field, textInputStyle, Notice, TrustFooter, FullPageLoader,
} from './ui';

export default function ResetPassword({ onDone }) {
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setHasSession(Boolean(data?.session));
      setReady(true);
    });
    return () => { alive = false; };
  }, []);

  // The full list, first item shown. passwordProblems() carries the founder's
  // five rules plus the three that complexity rules alone never catch: a known
  // weak password, one built from the signed-in address, and one that repeats
  // four characters.
  const verdict = passwordProblems(password, {});
  const problem = touched && !verdict.ok ? verdict.problems[0] : null;
  const mismatch = touched && confirm && password !== confirm
    ? 'The two passwords are not the same.'
    : null;

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (!verdict.ok || password !== confirm) return;

    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(readableEmailError(updateError, 'That password could not be set.'));
        return;
      }
      setDone(true);
    } catch {
      setError('Orchid could not reach the network just now. Your password is unchanged.');
    } finally {
      setBusy(false);
    }
  }

  if (!ready) return <FullPageLoader message="Checking your link…" />;

  return (
    <main style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '48px 20px', background: O.canvas, fontFamily: SANS,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ marginBottom: 26, textAlign: 'center' }}>
          <img
            src="/images/orchid-logo-green.png"
            alt="Orchid"
            width={64}
            height={64}
            style={{ borderRadius: '50%', objectFit: 'cover', display: 'inline-block' }}
            onError={(e) => { e.target.style.visibility = 'hidden'; }}
          />
          <div style={{
            marginTop: 10, fontFamily: DISPLAY, fontSize: 20, color: O.green,
            letterSpacing: '-0.01em',
          }}>Orchid Portal</div>
        </div>

        <Card>
          {done ? (
            <>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: O.ink, marginBottom: 8 }}>
                Your password is set.
              </h1>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: O.inkMuted, marginBottom: 18 }}>
                You are signed in on this device. Every other device holding a session for
                this account keeps it — sign out from those if the old password was known
                to somebody else.
              </p>
              <PrimaryCTA onClick={onDone}>Go to the portal</PrimaryCTA>
            </>
          ) : !hasSession ? (
            <>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: O.ink, marginBottom: 8 }}>
                This link is no longer valid.
              </h1>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: O.inkMuted, marginBottom: 18 }}>
                A recovery link is single-use and expires. Nothing has changed on the
                account. Ask for another from the sign-in screen.
              </p>
              <PrimaryCTA onClick={onDone}>Back to sign in</PrimaryCTA>
            </>
          ) : (
            <form onSubmit={submit} noValidate>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: O.ink, marginBottom: 4 }}>
                Set a new password
              </h1>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: O.inkMuted, marginBottom: 18 }}>
                At least eight characters, with a capital, a number and a special character.
              </p>

              <Field label="New password" htmlFor="new-password" error={problem}>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched(true)}
                  aria-invalid={Boolean(problem)}
                  style={textInputStyle(Boolean(problem))}
                />
              </Field>

              <Field label="Type it again" htmlFor="confirm-password" error={mismatch}>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onBlur={() => setTouched(true)}
                  aria-invalid={Boolean(mismatch)}
                  style={textInputStyle(Boolean(mismatch))}
                />
              </Field>

              {error && <Notice tone="critical">{error}</Notice>}

              <div style={{ marginTop: 18 }}>
                <PrimaryCTA type="submit" busy={busy}
                            disabled={busy || !verdict.ok || password !== confirm}>
                  Set my password
                </PrimaryCTA>
              </div>
            </form>
          )}
        </Card>

        <TrustFooter style={{ marginTop: 22 }} />
      </div>
    </main>
  );
}
